import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import api from '../services/api.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import { formatCurrency } from '../utils/currency.ts';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  TruckIcon,
  BanknotesIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

// Types
interface Asset {
  id: string;
  description: string;
  type: string;
  acquisitionValue: number;
  amountPaid: number;
  currentValue: number;
  acquisitionDate: string;
  status: 'active' | 'inactive' | 'sold' | 'exchanged';
}

// Aluguéis não alteram o status do ativo; filtros baseiam-se apenas no status do ativo

const Assets: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    assetId: string;
    assetDescription: string;
  }>({
    isOpen: false,
    assetId: '',
    assetDescription: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Função para converter tipo em texto legível
  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'real_estate':
        return 'Imóvel';
      case 'other':
        return 'Outros';
      default:
        return type;
    }
  };

  // Função para garantir que valores sejam números válidos
  const safeNumber = (value: any): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const {
    data: assets,
    isLoading,
    error,
    refetch,
  } = useQuery<Asset[]>(
    'assets',
    async () => {
      const response = await api.get('/assets');
      return response.data;
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Filtrar ativos baseado na busca, tipo e status

  const filteredAssets = assets
    ? assets.filter(asset => {
        const matchesSearch =
          asset.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.type.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || asset.type === filterType;
        const matchesStatus =
          filterStatus === 'all'
            ? true
            : filterStatus === 'active'
              ? asset.status === 'active'
              : filterStatus === 'inactive'
                ? asset.status === 'inactive'
                : filterStatus === 'sold'
                  ? asset.status === 'sold'
                  : filterStatus === 'exchanged'
                    ? asset.status === 'exchanged'
                    : true;
        return matchesSearch && matchesType && matchesStatus;
      })
    : [];

  const handleViewDetails = (id: string) => {
    navigate(`/assets/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/assets/${id}/edit`);
  };

  const handleDelete = (id: string, description: string) => {
    setDeleteModal({
      isOpen: true,
      assetId: id,
      assetDescription: description,
    });
  };

  const confirmDelete = async () => {
    if (!deleteModal.assetId) return;

    setIsDeleting(true);
    try {
      await api.delete(`/assets/${deleteModal.assetId}`);
      refetch();
      setDeleteModal({
        isOpen: false,
        assetId: '',
        assetDescription: '',
      });
    } catch (error) {
      console.error('Erro ao excluir ativo:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setDeleteModal({
        isOpen: false,
        assetId: '',
        assetDescription: '',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Erro ao carregar ativos. Por favor, tente novamente.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 mr-3 text-primary-600" />
            Patrimônio
          </h1>
          <p className="text-gray-600 mt-2">Gerencie seus ativos e investimentos</p>
        </div>
        <button
          onClick={() => navigate('/assets/create')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Ativo
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{filteredAssets?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor de Aquisição</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(filteredAssets?.reduce((sum, asset) => sum + safeNumber(asset.acquisitionValue), 0) || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Atual</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(filteredAssets?.reduce((sum, asset) => sum + safeNumber(asset.currentValue), 0) || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar ativos..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Type Filter */}
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os tipos</option>
              <option value="real_estate">Imóvel</option>
              <option value="other">Outros</option>
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="sold">Vendido</option>
              <option value="exchanged">Permutado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid de Ativos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAssets.map(asset => (
          <div
            key={asset.id}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {asset.description}
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {getTypeLabel(asset.type)}
                  </span>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    asset.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : asset.status === 'inactive'
                        ? 'bg-yellow-100 text-yellow-800'
                        : asset.status === 'exchanged'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-red-100 text-red-800'
                  }`}
                >
                  {asset.status === 'active'
                    ? 'Ativo'
                    : asset.status === 'inactive'
                      ? 'Inativo'
                      : asset.status === 'exchanged'
                        ? 'Permutado'
                        : 'Vendido'}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor de Aquisição:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(asset.acquisitionValue)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor Pago:</span>
                  <span className="text-sm font-medium text-blue-600">
                    {asset.amountPaid ? formatCurrency(asset.amountPaid) : formatCurrency(0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor Atual:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {asset.currentValue ? formatCurrency(asset.currentValue) : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">ROI:</span>
                  <span className={`text-sm font-medium ${
                    (asset.amountPaid || 0) > 0
                      ? ((asset.currentValue - asset.acquisitionValue) / asset.amountPaid) * 100 > 0
                        ? 'text-green-600'
                        : 'text-red-600'
                      : 'text-gray-900'
                  }`}>
                    {(asset.amountPaid || 0) > 0 ? (
                      `${(((asset.currentValue - asset.acquisitionValue) / asset.amountPaid) * 100).toFixed(2)}%`
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Data de Aquisição:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(asset.acquisitionDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleViewDetails(asset.id)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                <button
                  onClick={() => handleEdit(asset.id)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                {hasPermission('assets.delete') && (
                  <button
                    onClick={() => handleDelete(asset.id, asset.description)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredAssets.length === 0 && (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum ativo encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterType !== 'all'
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece criando um novo ativo.'}
          </p>
          {!searchTerm && filterType === 'all' && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/assets/create')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Novo Ativo
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Ativo"
        message={`Tem certeza que deseja excluir o ativo "${deleteModal.assetDescription}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Assets;
